<template>
  <div>
    <!-- Text that displays 'LAT/LON' or 'LON/LAT' -->
    <div class="lat-lon-text">
      {{ format ? 'LAT/LON' : 'LON/LAT' }}
    </div>

    <!-- Text that displays the actual coordinates -->
    <div id="coordinatesDisplay" class="coordinates-text" @click="$emit('click')">
      <p v-if="lat == 0 && lon == 0" class="no-drag coordinates-placeholder">
        RIGHT CLICK MAP
      </p>
      <p v-else-if="format" class="no-drag">{{ lat }},<br />{{ lon }}</p>
      <p v-else-if="!format" class="no-drag">{{ lon }},<br />{{ lat }}</p>
    </div>

    <!-- Button for swapping the coordinates' formatting -->
    <div class="coords-format swap-coords-format">
      <fa
        :icon="['fas', 'exchange-alt']"
        :title="coordinateInput ? 'Clear Input to swap format' : 'Swap Format'"
        @click="() => {this.format = !this.format}"
      />
    </div>

    <!-- Button for manually copying coordinates to clipboard -->
    <div
      :class="
        'coords-format copy-coords-format' +
        (lat == 0 && lon == 0 ? ' copy-coords-format--disabled' : '')
      "
    >
      <fa
        :icon="['fas', clipboardIcon]"
        :class="clipboardIcon == 'clipboard-check' ? 'green-check' : ''"
        title="Copy Coordinates"
        @click="copyCoords()"
      />

      <span v-if="clipboardIcon == 'clipboard-check' && !showToggle" class="copied-text">COPIED</span>

      <MMP_LatLongToggle
        v-else-if="showToggle && (ansiComponent || drtComponents)"
        ref="latLongToggle"
        class="toggle-position"
        @toggleChange="$emit('toggleChange', $event)"
      />
    </div>

    <!-- Text input field for the coordinates -->
    <div :class="'coordinates-input-field' + (expanded ? ' active' : '')">
      <b-row @keyup.enter="zoomToCoords()">
        <b-col>
          <b-form-input
            v-model="coordinateInput"
            :placeholder="
              format ? 'Latitude, Longitude' : 'Longitude, Latitude'
            "
            class="no-drag"
            ref="coordsInput"
          ></b-form-input>
        </b-col>
        <b-col cols="2" class="no-drag">
          <b-button @click="zoomToCoords()" squared class="go-btn"> Go </b-button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<style scoped>
.copied-text {
  position: absolute;
  top: 10px;
  left: 18px;
  color: white;
  font-size: 11px;
}
.go-btn {
  color: white;
  background-color: rgb(21.45, 140.4, 87.1);
  border-color: rgb(21.45, 140.4, 87.1);
}
.green-check {
  color: rgb(21.45, 140.4, 87.1);
}
/* Scales with viewport width: 210px on small screens up to 300px on large */
.coordinates-input-field {
  max-width: clamp(210px, 15vw, 300px);
}
/* CSS unrelated to screen width */
.coordinates-input-field {
  color: white;
  padding-top: 5px;
  position: absolute;
  left: 30px;
  top: 32px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 100ms ease, visibility 0s ease 100ms;
}
.coordinates-input-field.active {
  opacity: 1;
  visibility: visible;
  transition: opacity 1.25s ease, visibility 0s ease 0s;
}
.coordinates-placeholder {
  position: absolute;
  font-size: 11px;
  color: #b5b5b5;
  top: 20px;
}
.coordinates-text {
  color: white;
  cursor: pointer;
  font-size: 15px;
  text-align: right;
  padding-top: 5px;
  position: absolute;
  height: 60px;
  width: 98px;
  right: 6px;
  top: 25px;
}
.form-control {
  background-color: #063c6d;
  border: 3px solid white;
  color: white;
}
.form-control::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: white;
  opacity: 1;
}
:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: white;
}
::-ms-input-placeholder {
  /* Microsoft Edge */
  color: white;
}
.lat-lon-text {
  color: white;
  font-size: 13px;
  padding-top: 5px;
  position: absolute;
  right: 50px;
}
.coords-format {
  color: white;
  font-size: 17px;
  padding-top: 5px;
  position: absolute;
  bottom: 0px;
  cursor: pointer;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
}
.swap-coords-format {
  right: 10px;
}
.copy-coords-format {
  cursor: pointer;
  right: 88px;
}
.copy-coords-format--disabled {
  color: #b5b5b5;
}
.fa-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.fa-enabled {
  cursor: pointer;
}

.toggle-position {
  position: absolute;
  top: -10px;
  left: 18px;
  color: white;
  font-size: 11px;
}
</style>

<script>
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style';
import CircleStyle from 'ol/style/Circle';
import RegularShape from 'ol/style/RegularShape';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import {fromLonLat, transform} from 'ol/proj'
import MMP_LatLongToggle from './MMP_LatLongToggle.vue'
export default {
  name: 'MMP_CollapsedCoordinates',
  props: ['showToggle', 'ansiComponent', 'drtComponents', 'coords', 'expanded', 'map', 'pointVectorSource'],
  components: {
    MMP_LatLongToggle
  },
  data() {
    return {
      clipboardIcon: 'clipboard',
      clipboardIconTimeout: null,
      coordinateInput: '',
      format: false,
      lat: 0,
      lon: 0,
      vectorSource: new VectorSource({})
    }
  },
  methods: {
    copyCoords() {
      if (!(this.lat == 0 && this.lon == 0)) {
        navigator.clipboard.writeText(
          this.format ? `${this.lat}, ${this.lon}` : `${this.lon}, ${this.lat}`
        )
        this.clipboardIcon = 'clipboard-check'
        if (this.clipboardIconTimeout == null) {
          this.initClipboardIconTimeout()
        } else {
          clearTimeout(this.clipboardIconTimeout)
          this.initClipboardIconTimeout()
        }
      }
    },
    initClipboardIconTimeout() {
      this.clipboardIconTimeout = setTimeout(() => {
        if (this.clipboardIcon == 'clipboard-check') {
          this.clipboardIcon = 'clipboard'
        }
      }, 4000)
    },
    initVectorLayer() {
      let vectorLayer = new VectorLayer({
        source: this.pointVectorSource
      })
      vectorLayer.setZIndex(999999999)
      this.$emit('getVectorlayer', vectorLayer)
    },
    async zoomToCoords() {
      if (!this.coordinateInput.includes(',')) {
        let formatText = this.format ? 'lat, lon' : 'lon, lat'
        this.$bvToast.toast(`Format should be '${formatText}'.`, {
          autoHideDelay: 7000,
          title: 'Error: Invalid Coordinates.',
          toaster: 'b-toaster-top-right'
        })
        return
      }

      // REFACTOR: Refactor the below code to have its own function
      let coords = this.coordinateInput.split(',')
      let x, y, errX, errY, errMsg
      if (this.format) {
        y = parseFloat(coords[0])
        x = parseFloat(coords[1])
      } else {
        x = parseFloat(coords[0])
        y = parseFloat(coords[1])
      }
      errX = x > 180 || x < -180
      errY = y > 90 || y < -90
      errMsg = 'Please Enter Valid '
      if (errX && errY) {
        errMsg += 'Latitude and Longitude'
      } else if (errX) {
        errMsg += 'Longitude'
      } else if (errY) {
        errMsg += 'Latitude'
      }
      errMsg += '.'
      if (errX || errY) {
        this.$bvToast.toast(errMsg, {
          autoHideDelay: 7000,
          title: 'Error: Invalid Coordinates.',
          toaster: 'b-toaster-top-right'
        })
        return
      }
      let newCenter = new transform([x, y], 'EPSG:4326', 'EPSG:3857')
      this.map.getView().animate({zoom: 18}, {center: newCenter})
      
      // Define custom style for the geoMarker
      const hexagonStyle = new Style({
        image: new RegularShape({
          points: 6, // Number of points for the hexagon
          radius: 7, // Radius of the hexagon
          fill: new Fill({ color: 'rgba(6, 60, 109, 0.2)' }), // Fill color
          stroke: new Stroke({ color: '#00008B', width: 2 }), // Border color and width
        }),
      });
      
      let geoMarker = new Feature({
        type: 'geoMarker',
        geometry: new Point(new fromLonLat([x, y])),
        id: "latLongPointer"
      })

      geoMarker.setStyle(hexagonStyle);

      await this.$emit('addPoint', geoMarker)
      await this.$emit('showToggle')
      
      this.lat = y
      this.lon = x

      if (this.showToggle && (this.ansiComponent || this.drtComponents)) {
        this.$refs.latLongToggle.toggleTrue()
      }

      // Closes the coordinate feature component
      document.getElementById('coordinatesDisplay').click()
    }
  },
  mounted() {
    if (sessionStorage.token) {
      this.initVectorLayer()

      window.addEventListener('keydown', (event) => {
        if (['input', 'b-form-textarea', 'b-form-input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
          return;
        }
        if (event.ctrlKey || event.metaKey) {
          return;
        }
        // Prevent the default action of adding 'C' or 'c' to the input
        if (event.key === 'C' || event.key === 'c') {
          if (this.expanded) {
            this.$emit('click')
            return;
          }

          // Check if the target is not already an input or textarea
          if (!['input', 'b-form-textarea', 'b-form-input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
            this.$emit('click');

            event.preventDefault();

            this.$nextTick(() => {
              this.$refs.coordsInput.focus();
              this.coordinateInput = ''; // Ensure the input is cleared
            });
          }
        }
      })
    } else {
      this.$router.push({name: 'Login'})
    }
  },
  watch: {
    coords: function (newValue, oldValue) {
      this.lat = newValue[1].toFixed(6)
      this.lon = newValue[0].toFixed(6)
      this.copyCoords()
    },
    format: function (newValue, oldValue) {
      // REFACTOR
      // Check if we need to check the values of newValue and oldValue 
      this.clipboardIcon = 'clipboard'
    }
  }
}
</script>
